import { MutationHookOptions, QueryHookOptions, gql, useMutation, useQuery } from '@apollo/client'
import {
  Mutation,
  MutationCreateTiersArgs,
  MutationTierArchivedToggleArgs,
  MutationUpdateTierCatalogPartPriceArgs,
  MutationUpdateTierRulesArgs,
  MutationUpdateTiersArgs,
  Pagination,
  Query,
  QueryTierArgs,
  QueryTiersArgs,
  SupplierOpportunitiesResult,
  SupplierOpportunity,
  TierCatalogPartTiersPaginatedArgs,
} from '../gen/models'

export const TierFragment = gql`
  fragment TierFragment on Tier {
    id
    name
    customerId
    booleanOperator
    discountValue
    discountMetric
    discountPeriod
  }
`

export const TierRebateFragment = gql`
  fragment TierRebateFragment on TierRebate {
    id
    name
    description
    type
    periodType
    scope
    value
    baselineSpend
    productTsIds
    payoutLevel
    vendors
    effectiveDate
    expirationDate
    metricType
    conditionalStartDate
    createdAt
    updatedAt
    advancedRulesJsonLogic
    rebateGroupId
    rebateTierIndex
    contractsTiersRebates {
      tiersId
    }
  }
`

/**
 * A rebate group is 1..N tiers. Ungrouped rebates come back as a group of one, so the UI renders
 * every rebate through the same path.
 *
 * `id` is the group header row's id — pass it back as `groupId` when editing so the group keeps its
 * identity. `tiers` carries each member's own id, which the upsert needs in order to update rows
 * rather than recreate them.
 */
export const RebateGroupFragment = gql`
  fragment RebateGroupFragment on RebateGroup {
    id
    name
    description
    type
    periodType
    scope
    payoutLevel
    conditionalStartDate
    effectiveDate
    expirationDate
    productTsIds
    vendors
    isGroup
    isTiered
    tierCount
    contractsTiersRebates {
      tiersId
    }
  }
`

export const TierRuleFragment = gql`
  fragment TierRuleFragment on TierRule {
    id
    ruleType
    metricType
    periodType
    operator
    valueFirst
    valueSecond
    scope
    basketIds
    vendors
    productTsIds
  }
`

const CREATE_TIER = gql`
  mutation createTiers($input: [CreateTierInput!]!) {
    createTiers(input: $input) {
      ...TierFragment
    }
  }
  ${TierFragment}
`

export function useCreateTierMutation(
  options?: MutationHookOptions<Pick<Mutation, 'createTiers'>, MutationCreateTiersArgs>,
) {
  return useMutation<Pick<Mutation, 'createTiers'>, MutationCreateTiersArgs>(CREATE_TIER, options)
}

const UPDATE_TIER = gql`
  mutation updateTiers($input: [UpdateTierInput!]!) {
    updateTiers(input: $input) {
      ...TierFragment
    }
  }
  ${TierFragment}
`

export function useUpdateTierMutation(
  options?: MutationHookOptions<Pick<Mutation, 'updateTiers'>, MutationUpdateTiersArgs>,
) {
  return useMutation<Pick<Mutation, 'updateTiers'>, MutationUpdateTiersArgs>(UPDATE_TIER, options)
}

const UPDATE_TIER_RULES = gql`
  mutation updateTierRules($input: UpdateTierRuleInput!) {
    updateTierRules(input: $input) {
      ...TierFragment
      rules {
        ...TierRuleFragment
      }
    }
  }
  ${TierFragment}
  ${TierRuleFragment}
`

export function useUpdateTierRulesMutation(
  options?: MutationHookOptions<Pick<Mutation, 'updateTierRules'>, MutationUpdateTierRulesArgs>,
) {
  return useMutation<Pick<Mutation, 'updateTierRules'>, MutationUpdateTierRulesArgs>(UPDATE_TIER_RULES, options)
}

export const TIER_OPTIONS_QUERY = gql`
  query tierOptions($input: GetTiersByCriteriaInput!, $pagination: Pagination!, $sort: TiersSortInput) {
    tiers(input: $input, pagination: $pagination, sort: $sort) {
      totalHits
      hits {
        id
        name
      }
    }
  }
`

export const useTierOptionsQuery = (options?: QueryHookOptions<Pick<Query, 'tiers'>, QueryTiersArgs>) =>
  useQuery(TIER_OPTIONS_QUERY, options)

export const CREATE_TIERS = gql`
  mutation createCatalogTiers($input: [CreateTierInput!]!) {
    createTiers(input: $input) {
      id
      name
    }
  }
`

export const useCreateCatalogTiersMutation = (
  options?: MutationHookOptions<Pick<Mutation, 'createTiers'>, MutationCreateTiersArgs>,
) => useMutation(CREATE_TIERS, options)

export const TIERS_QUERY = gql`
  query tiers($input: GetTiersByCriteriaInput!, $pagination: Pagination!, $sort: TiersSortInput) {
    tiers(input: $input, pagination: $pagination, sort: $sort) {
      totalHits
      hits {
        id
        name
        createdAt
        updatedAt
        description
        booleanOperator
        rules {
          ruleType
          periodType
          metricType
          operator
          valueFirst
          valueSecond
        }
      }
    }
  }
`

export function useTiersQuery(options: QueryHookOptions<Pick<Query, 'tiers'>, QueryTiersArgs>) {
  return useQuery<Pick<Query, 'tiers'>, QueryTiersArgs>(TIERS_QUERY, options)
}

export const TIER_ARCHIVED_TOGGLE = gql`
  mutation tierArchivedToggle($input: IDArgs!) {
    tierArchivedToggle(input: $input) {
      id
    }
  }
`

export function useTierArchivedToggleMutation(
  options?: MutationHookOptions<Pick<Mutation, 'tierArchivedToggle'>, MutationTierArchivedToggleArgs>,
) {
  return useMutation<Pick<Mutation, 'tierArchivedToggle'>, MutationTierArchivedToggleArgs>(
    TIER_ARCHIVED_TOGGLE,
    options,
  )
}

// Manage Tiers workspace (#7335, spec §2.2). The operation name is `updateTiersBulk` (≠ the field
// `updateTiers`, which master's UPDATE_TIER already claims as an op name) — graphql-codegen requires
// globally-unique operation names. Selection is `{ id }` only; the workspace refetches TIERS_QUERY on
// success. Exported so the recording-link test can match the real document (react-conventions §14.1).
export const UPDATE_TIERS_BULK = gql`
  mutation updateTiersBulk($input: [UpdateTierInput!]!) {
    updateTiers(input: $input) {
      id
    }
  }
`

export function useUpdateTiersBulkMutation(
  options?: MutationHookOptions<Pick<Mutation, 'updateTiers'>, MutationUpdateTiersArgs>,
) {
  return useMutation<Pick<Mutation, 'updateTiers'>, MutationUpdateTiersArgs>(UPDATE_TIERS_BULK, options)
}

// ── Tier detail (#7337, spec §2) — tier-scoped ops, co-located with the other TierResolver docs. ──

/** Summary tab (§2.1). Consumers pass fetchPolicy:'network-only' + onCompleted at the call site. */
export const TIER_DETAIL_QUERY = gql`
  query tierDetail($id: String!) {
    tier(id: $id) {
      id
      name
      description
      booleanOperator
      createdAt
      updatedAt
      rules {
        id
        ruleType
        periodType
        operator
        valueFirst
        valueSecond
        metricType
        createdAt
        updatedAt
      }
      children {
        id
        name
        description
        createdAt
        booleanOperator
        rules {
          ruleType
          periodType
          operator
          valueFirst
          valueSecond
          metricType
        }
        offer {
          id
          name
          status
          parentId
          parent {
            name
          }
        }
      }
    }
  }
`

export function useTierDetailQuery(options?: QueryHookOptions<Pick<Query, 'tier'>, QueryTierArgs>) {
  return useQuery<Pick<Query, 'tier'>, QueryTierArgs>(TIER_DETAIL_QUERY, options)
}

/** Catalog Parts tab (§2.2). `Pagination!` is non-null on the ResolveField — always sent. */
export const TIER_CATALOG_PARTS_QUERY = gql`
  query tierCatalogParts(
    $id: String!
    $input: CatalogPartTiersFilterInput
    $pagination: Pagination!
    $sorts: [CatalogPartsSortInput!]
  ) {
    tier(id: $id) {
      id
      name
      catalogPartTiersPaginated(pagination: $pagination, sorts: $sorts, input: $input) {
        totalHits
        hits {
          partsTsId
          price
          uom
          uomConversion
          part {
            tsId
            manufacturer
            manufacturerItem
            vendor
            vendorItem
            description
          }
        }
      }
    }
  }
`

export function useTierCatalogPartsQuery(
  options?: QueryHookOptions<Pick<Query, 'tier'>, QueryTierArgs & TierCatalogPartTiersPaginatedArgs>,
) {
  return useQuery<Pick<Query, 'tier'>, QueryTierArgs & TierCatalogPartTiersPaginatedArgs>(
    TIER_CATALOG_PARTS_QUERY,
    options,
  )
}

/** Update-price mutation (§2.3). */
export const UPDATE_TIER_CATALOG_PART_PRICE = gql`
  mutation updateTierCatalogPartPrice($input: UpdateTierCatalogPartPriceInput!) {
    updateTierCatalogPartPrice(input: $input) {
      partsTsId
      uom
      uomConversion
      price
    }
  }
`

export function useUpdateTierCatalogPartPriceMutation(
  options?: MutationHookOptions<Pick<Mutation, 'updateTierCatalogPartPrice'>, MutationUpdateTierCatalogPartPriceArgs>,
) {
  return useMutation<Pick<Mutation, 'updateTierCatalogPartPrice'>, MutationUpdateTierCatalogPartPriceArgs>(
    UPDATE_TIER_CATALOG_PART_PRICE,
    options,
  )
}

// Tier conditions panel (#7336, spec §2.1). Operation name `updateTierRulesConditions` (≠ the field
// `updateTierRules`, which master's dead UPDATE_TIER_RULES already claims as an op name) —
// graphql-codegen requires globally-unique operation names. Selection is `{ id }` only; the workspace
// refetches TIERS_QUERY via the onDone seam. Exported for the recording-link test (react-conventions §14.1).
export const UPDATE_TIER_RULES_CONDITIONS = gql`
  mutation updateTierRulesConditions($input: UpdateTierRuleInput!) {
    updateTierRules(input: $input) {
      id
    }
  }
`

export function useUpdateTierRulesConditionsMutation(
  options?: MutationHookOptions<Pick<Mutation, 'updateTierRules'>, MutationUpdateTierRulesArgs>,
) {
  return useMutation<Pick<Mutation, 'updateTierRules'>, MutationUpdateTierRulesArgs>(
    UPDATE_TIER_RULES_CONDITIONS,
    options,
  )
}

// ── Cloned Tiers reachability probe (#7751, spec §2.1) ──

/**
 * #7751 — Cloned Tiers reachability probe. Asks the server which of the table's parent
 * opportunity ids THIS caller can open (the server's own P3/P4 predicates, caller's own
 * token). Two aliases because the listing's `input` always attaches one arm of the
 * archived predicate (offer.repository.ts:205-207) while the singular detail read
 * applies none (getSupplierOpportunityACL passes no input) — the union of
 * isArchived:false and isArchived:true replicates the singular read exactly.
 * Divergent operation name over the existing field: codegen-validation-only, no
 * SDL/genType output delta (react-conventions §23.3).
 */
export const SUPPLIER_OPPORTUNITIES_REACHABILITY_QUERY = gql`
  query supplierOpportunitiesReachability($opportunityIds: [String!]!, $pagination: Pagination) {
    active: supplierOpportunities(
      input: { opportunityIds: $opportunityIds, isArchived: false }
      pagination: $pagination
    ) {
      totalHits
      hits {
        id
      }
    }
    archived: supplierOpportunities(
      input: { opportunityIds: $opportunityIds, isArchived: true }
      pagination: $pagination
    ) {
      totalHits
      hits {
        id
      }
    }
  }
`

type ReachabilityHits = Pick<SupplierOpportunitiesResult, 'totalHits'> & {
  hits: Array<Pick<SupplierOpportunity, 'id'>>
}

/**
 * The aliases make `Pick<Query, 'supplierOpportunities'>` unusable as the result type, so the shape is
 * hand-maintained here — the #7339 `SupplierOpportunityDetail` precedent. The member types still come
 * from `gen/models`, so a schema rename breaks the compile; the DOCUMENT's half of the pairing is
 * locked by TierDetail.test.tsx's TD-11 against the AST. Keep both halves.
 */
export type SupplierOpportunitiesReachability = {
  active: ReachabilityHits
  archived: ReachabilityHits
}

type SupplierOpportunitiesReachabilityVariables = {
  opportunityIds: string[]
  pagination: Pagination
}

/** `options` is REQUIRED (unlike this module's neighbours): the consumer always passes `variables`. */
export function useSupplierOpportunitiesReachabilityQuery(
  options: QueryHookOptions<SupplierOpportunitiesReachability, SupplierOpportunitiesReachabilityVariables>,
) {
  return useQuery<SupplierOpportunitiesReachability, SupplierOpportunitiesReachabilityVariables>(
    SUPPLIER_OPPORTUNITIES_REACHABILITY_QUERY,
    options,
  )
}
